/**
 * Authentication context for Ballast Payroll.
 *
 * The JWT access token is held in memory only (React state) — never localStorage — and the
 * 7-day refresh token is an httpOnly cookie managed by the server, so an XSS has nothing to
 * steal. (Design review 2026-07-15, P0-1.)
 *
 * On mount the session is bootstrapped from the refresh cookie: we request a fresh access
 * token and, if we get one, load the user/org from GET /api/auth/me. A full page reload thus
 * restores the session via the cookie without persisting any token in JS-readable storage.
 *
 * @module context/AuthContext
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
  type ReactElement,
} from 'react';
import type { AuthUser, AuthOrg } from '../types';
import { refreshAccessToken, BASE_URL } from '../api/client';

// ── Context shape ─────────────────────────────────────────────────────────────

interface AuthContextValue {
  /** JWT access token; null when not authenticated. */
  token: string | null;
  /** Authenticated user profile; null when not authenticated. */
  user: AuthUser | null;
  /** Authenticated organization; null when not authenticated. */
  org: AuthOrg | null;
  /** True while the initial token validation is in-flight on mount. */
  isLoading: boolean;
  /**
   * Store the access token (in memory) and user/org data after a successful login.
   * The refresh token is an httpOnly cookie set by the server — not passed here.
   * Called by the Login/Register/AcceptInvite pages after auth succeeds.
   */
  login: (token: string, user: AuthUser, org: AuthOrg) => void;
  /** Revoke the session server-side (clears the cookie) and drop all local auth state. */
  logout: () => void;
}

// ── Create context ────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Wraps the application and provides authentication state to all descendants.
 *
 * On mount, if a token is found in localStorage, it is validated against
 * GET /api/auth/me. If validation fails (expired/invalid), the token is
 * removed and the user must log in again.
 */
export function AuthProvider({ children }: AuthProviderProps): ReactElement {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [org, setOrg] = useState<AuthOrg | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Clears in-memory session state only (no server call). Used by the refresh-failure signal.
  const clearSession = useCallback((): void => {
    setToken(null);
    setUser(null);
    setOrg(null);
  }, []);

  // User-initiated logout: revoke the refresh token server-side (which also clears the cookie),
  // then drop local state. Fire-and-forget — local state clears regardless of the network call.
  // The localStorage write broadcasts the logout to OTHER TABS (the 'storage' event only fires
  // in non-originating tabs) so they drop their in-memory sessions immediately instead of at
  // their next failed refresh (P2-8). No token material is stored — just a timestamp.
  const logout = useCallback((): void => {
    void fetch(`${BASE_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' }).catch(
      () => undefined,
    );
    try {
      localStorage.setItem('sbp:logout-broadcast', String(Date.now()));
    } catch {
      // Storage unavailable (private mode quota etc.) — cross-tab sync degrades
      // gracefully to the refresh-failure path.
    }
    clearSession();
  }, [clearSession]);

  const login = useCallback((t: string, u: AuthUser, o: AuthOrg): void => {
    // Access token lives in memory only; the refresh token is an httpOnly cookie set by the
    // server. Nothing is written to localStorage.
    setToken(t);
    setUser(u);
    setOrg(o);
  }, []);

  // Keep the in-memory access token in sync with the API client's silent refresh, clear
  // the session when a refresh ultimately fails ('sbp:logout'), and mirror a logout made
  // in ANOTHER tab (the 'storage' event fires here when that tab writes the broadcast
  // key; the server has already revoked the shared refresh-token family).
  useEffect((): (() => void) => {
    const onRefresh = (e: Event): void => setToken((e as CustomEvent<string>).detail);
    const onLogout = (): void => clearSession();
    const onStorage = (e: StorageEvent): void => {
      if (e.key === 'sbp:logout-broadcast') clearSession();
    };
    window.addEventListener('sbp:token', onRefresh);
    window.addEventListener('sbp:logout', onLogout);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('sbp:token', onRefresh);
      window.removeEventListener('sbp:logout', onLogout);
      window.removeEventListener('storage', onStorage);
    };
  }, [clearSession]);

  // Bootstrap the session on mount from the httpOnly refresh cookie: request a fresh access
  // token and, if we get one, load the user/org from /api/auth/me. No cookie → not logged in.
  useEffect((): void => {
    const bootstrap = async (): Promise<void> => {
      try {
        const fresh = await refreshAccessToken();
        if (!fresh) return;
        const res = await fetch(`${BASE_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${fresh}` },
          credentials: 'include',
        });
        const json = (await res.json()) as {
          success: boolean;
          data?: { user: AuthUser; org: AuthOrg };
        };
        if (res.ok && json.success && json.data) {
          setToken(fresh);
          setUser(json.data.user);
          setOrg(json.data.org);
        }
      } catch {
        // network error — leave unauthenticated
      } finally {
        setIsLoading(false);
      }
    };
    void bootstrap();
  }, []);

  return (
    <AuthContext.Provider value={{ token, user, org, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Returns the current authentication context.
 *
 * Must be used inside an <AuthProvider>.
 *
 * @throws {Error} if called outside of AuthProvider
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
